export interface CarListItem {
  id: string;
  stock_number: string;
  year: number | null;
  make: string;
  model: string;
  trim: string;
  body_style: string;
  price: number | null;
  list_price?: number | null;
  is_special_price?: boolean;
  odometer_km: number | null;
  is_sold: boolean;
  cover_photo_url: string | null;
  photo_count: number;
}

export interface CarPhoto {
  order: number;
  url: string;
}

export interface CarDetail extends Omit<CarListItem, 'photo_count'> {
  vin: string;
  photos: CarPhoto[];
}

export interface CarListResponse {
  cars: CarListItem[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
  status: CarListingStatus;
  counts: CarListingCounts;
  facets: {
    makes: string[];
    models: string[];
    years: number[];
    body_styles: string[];
    /** SAN Autohub style: Model options for the selected Make. */
    models_by_make?: Record<string, string[]>;
    years_by_make?: Record<string, number[]>;
  };
}

export type CarListingStatus = 'for_sale' | 'sold' | 'all';

export interface CarListingCounts {
  for_sale: number;
  sold: number;
  all: number;
}

export type CarSort = 'newest' | 'price_asc' | 'price_desc' | 'year_desc' | 'km_asc';

export const CAR_STATUS_OPTIONS: { value: CarListingStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'for_sale', label: 'For sale' },
  { value: 'sold', label: 'Sold' },
];

export const CAR_SORT_OPTIONS: { value: CarSort; label: string }[] = [
  { value: 'newest', label: 'Newest listings' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'year_desc', label: 'Year: newest first' },
  { value: 'km_asc', label: 'Kilometres: lowest first' },
];

const priceFormatter = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
});

export function formatKmFilter(km: number): string {
  return `${km.toLocaleString('en-CA')} km`;
}

export function formatCarPriceAmount(price: number): string {
  return priceFormatter.format(Math.max(0, price));
}

export interface CarNumericRange {
  min: number;
  max: number;
}

function finiteNumbers(values: Array<number | null | undefined>): number[] {
  return values.filter((value): value is number => value != null && Number.isFinite(value));
}

/** SAN Autohub style: price slider starts at $0 and ends at the highest listed price. */
export function inventoryPriceExtent(cars: Array<Pick<CarListItem, 'price'>>): CarNumericRange | null {
  const prices = finiteNumbers(cars.map((car) => car.price)).filter((price) => price > 0);
  if (!prices.length) return null;
  return { min: 0, max: Math.max(...prices) };
}

/** SAN Autohub style: odometer slider uses the actual km span in inventory. */
export function inventoryKmExtent(cars: Array<Pick<CarListItem, 'odometer_km'>>): CarNumericRange | null {
  const kms = finiteNumbers(cars.map((car) => car.odometer_km)).filter((km) => km >= 0);
  if (!kms.length) return null;
  const min = Math.min(...kms);
  const max = Math.max(...kms);
  return min === max ? { min, max: max + 1 } : { min, max };
}

export function carTitle(car: Pick<CarListItem, 'year' | 'make' | 'model'>): string {
  return [car.year, car.make, car.model].filter(Boolean).join(' ') || 'Vehicle listing';
}

export function formatCarPrice(price: number | null): string {
  return price && price > 0 ? priceFormatter.format(price) : 'Contact for price';
}

export function formatOdometer(km: number | null): string {
  return km === null || km === undefined ? '—' : `${km.toLocaleString('en-CA')} km`;
}

/** Dealer data stores some body styles in title case ("Suv"). */
function uniqSorted(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function mapEntry<T>(map: Record<string, T[]> | undefined, key: string): T[] {
  if (!map || !key) return [];
  const hit = Object.entries(map).find(([name]) => name.toLowerCase() === key.toLowerCase());
  return hit ? hit[1] : [];
}

/** Same idea as SAN Autohub `vehicleModel[make]`: models belong to the chosen make. */
export function modelsForMake(
  make: string,
  facets: CarListResponse['facets'],
  cars: Array<Pick<CarListItem, 'make' | 'model'>> = []
): string[] {
  if (!make) {
    return uniqSorted([
      ...Object.values(facets.models_by_make ?? {}).flat(),
      ...(facets.models ?? []),
    ]);
  }
  const fromCars = cars
    .filter((car) => car.make.toLowerCase() === make.toLowerCase())
    .map((car) => car.model);
  return uniqSorted([...mapEntry(facets.models_by_make, make), ...fromCars]);
}

/** Same idea as SAN Autohub `vehicleModelYear[make]`. */
export function yearsForMake(
  make: string,
  facets: CarListResponse['facets'],
  cars: Array<Pick<CarListItem, 'make' | 'year'>> = []
): number[] {
  const pooled = make
    ? [
        ...mapEntry(facets.years_by_make, make),
        ...cars
          .filter((car) => car.make.toLowerCase() === make.toLowerCase())
          .map((car) => car.year),
      ]
    : [...(facets.years ?? []), ...Object.values(facets.years_by_make ?? {}).flat()];
  const years = pooled.filter((year): year is number => typeof year === 'number' && Number.isFinite(year));
  const source = years.length > 0 ? years : make ? (facets.years ?? []) : years;
  return [...new Set(source)].sort((a, b) => b - a);
}

export function formatBodyStyle(style: string): string {
  if (!style) return '';
  return style.toLowerCase() === 'suv' ? 'SUV' : style.charAt(0).toUpperCase() + style.slice(1);
}

/** Tokens that only repeat doors/body, already shown as SEDAN / SUV / etc. */
const TRIM_DROP = new Set([
  '2dr',
  '3dr',
  '4dr',
  '5dr',
  '2-dr',
  '3-dr',
  '4-dr',
  '5-dr',
  'sdn',
  'sedan',
  'hb',
  'hatchback',
  'wgn',
  'wagon',
  'suv',
  'cpe',
  'coupe',
  'north',
  'america',
]);

const TRIM_EXPAND: Record<string, string> = {
  auto: 'Automatic',
  at: 'Automatic',
  man: 'Manual',
  mt: 'Manual',
  i4: '4-cylinder',
};

/**
 * SAN Autohub often stores a factory style code in `trim` (e.g. "4dr Auto DX-G")
 * instead of a marketing name ("Preferred"). Turn that into a readable label
 * and drop bits that only repeat body style.
 */
export function formatCarTrim(trim: string): string {
  if (!trim?.trim()) return '';
  const prepared = trim
    .replace(/\bw\/\s*/gi, 'with ')
    .replace(/(\d+)-Pass\b/gi, '$1-passenger')
    .replace(/\bPkg\b/gi, 'package');

  const kept = prepared
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((token) => {
      const key = token.toLowerCase();
      if (TRIM_DROP.has(key)) return [];
      return [TRIM_EXPAND[key] ?? token];
    });

  return kept.join(' ').replace(/\s+/g, ' ').trim();
}

/** Registered selling dealer. CWY is the mediator for this inventory. */
export const LISTING_DEALER = {
  name: 'SAN Autohub Inc.',
  city: 'Kitchener, ON',
} as const;

/** Prefill for homepage Sell Your Car. Same opener style as a listing inquiry. */
export const SELL_VEHICLE_DRAFT_MESSAGE = [
  'Hi, I am interested in selling my vehicle.',
  'Year / make / model:',
  'Odometer (km):',
].join('\n');

export function buildVehicleInquiryMessage(car: CarDetail): string {
  const title = carTitle(car);
  const lines = [
    car.is_sold
      ? `This listing is sold. Can you help me find something similar to the ${title}?`
      : `Hi, I am interested in this vehicle: ${title}.`,
    formatCarTrim(car.trim) && `Trim: ${formatCarTrim(car.trim)}`,
    car.stock_number && `Stock #: ${car.stock_number}`,
    car.vin && `VIN: ${car.vin}`,
    `Price: ${formatCarPrice(car.price)}`,
    car.odometer_km != null && `Odometer: ${formatOdometer(car.odometer_km)}`,
  ];
  return lines.filter(Boolean).join('\n');
}

export function vehicleChatTitle(car: Pick<CarDetail, 'year' | 'make' | 'model' | 'stock_number' | 'is_sold'>): string {
  const base = carTitle(car) || 'Vehicle';
  const stock = car.stock_number ? ` #${car.stock_number}` : '';
  return car.is_sold ? `Similar to ${base}${stock}` : `${base}${stock}`;
}
